import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import CharacterCard from "../components/CharacterCard";
import LazyImage from "../components/LazyImage";
import "./CharactersPage.css";

// Lazy load components not needed for initial render
const BirthdayDisplay = lazy(() => import("../components/BirthdayDisplay"));

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

// Skeleton UI for character loading
const CharacterCardSkeleton = () => (
  <div className="character-card skeleton">
    <div className="character-image-container">
      <div className="skeleton-image"></div>
    </div>
    <div className="character-details">
      <div className="skeleton-block" style={{ height: '24px', width: '70%' }}></div>
      <div className="skeleton-block" style={{ height: '18px', width: '50%' }}></div>
    </div>
  </div>
);

// CharactersPage skeleton UI for initial loading state
const CharactersPageSkeleton = () => (
  <div className="characters-page">
    <div className="characters-page-title-container">
      <h1 className="characters-page-title">Top 100 Characters</h1>
    </div>
    
    <div className="birthday-container skeleton">
      <div className="birthday-display">
        <div className="birthday-header">
          <div className="skeleton-block" style={{ height: '28px', width: '60%' }}></div>
        </div>
        <div className="birthday-content">
          <div className="skeleton-image" style={{ height: '150px', width: '150px' }}></div>
          <div className="birthday-details">
            <div className="skeleton-block" style={{ height: '24px', width: '80%' }}></div>
            <div className="skeleton-block" style={{ height: '18px', width: '60%' }}></div>
            <div className="skeleton-block" style={{ height: '18px', width: '70%' }}></div>
          </div>
        </div>
      </div>
    </div>
    
    <div className="characters-content">
      <div className="characters-grid">
        {Array(20).fill(0).map((_, index) => (
          <div className="character-item" key={index}>
            <div className="character-rank-badge skeleton-badge">#{index + 1}</div>
            <CharacterCardSkeleton />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// LazyCharacterCard component with Intersection Observer
const LazyCharacterCard = ({ character, index, indexOfFirstCharacter, animeStore }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div className="character-item" ref={cardRef}>
      <div className="character-rank-badge">
        #{indexOfFirstCharacter + index + 1}
      </div>
      {isVisible ? (
        <CharacterCard
          character={character}
          animeStore={animeStore}
        />
      ) : (
        <CharacterCardSkeleton />
      )}
    </div>
  );
};

const CharactersPage = () => {
  const [characters, setCharacters] = useState([]);
  const [birthdayCharacter, setBirthdayCharacter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [animeLoading, setAnimeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [animeStore, setAnimeStore] = useState({});
  const charactersPerPage = 20;

  // Fetch initial data with caching
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Check for cached data first
        const cachedData = localStorage.getItem('charactersPageData');
        const cachedTimestamp = localStorage.getItem('charactersPageTimestamp');
        
        // Use cached data if it's less than 6 hours old
        if (cachedData && cachedTimestamp) {
          const now = Date.now();
          const cacheTime = parseInt(cachedTimestamp);
          const sixHours = 6 * 60 * 60 * 1000;
          
          if (now - cacheTime < sixHours) {
            try {
              const { characters: cachedChars, birthdayChar, animeData } = JSON.parse(cachedData);
              setCharacters(cachedChars);
              setTotalPages(Math.ceil(cachedChars.length / charactersPerPage));
              setBirthdayCharacter(birthdayChar);
              setAnimeStore(animeData || {});
              setIsLoading(false);
              
              // Refresh birthday data anyway as it changes daily
              fetchBirthdayData();
              
              return;
            } catch (e) {
              console.error("Error parsing cached data:", e);
              // Continue with normal fetching if parsing fails
            }
          }
        }

        // Create promises for both requests to run in parallel
        const [charactersResponse, birthdayResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/characters/top?limit=100`),
          fetch(`${API_BASE_URL}/api/characters/birthdays/today`)
        ]);

        if (!charactersResponse.ok) {
          throw new Error(
            `Failed to fetch top characters: ${charactersResponse.statusText}`
          );
        }

        const charactersData = await charactersResponse.json();
        setCharacters(charactersData);
        setTotalPages(Math.ceil(charactersData.length / charactersPerPage));

        // Process birthday response separately
        let birthdayChar = null;
        if (birthdayResponse.ok) {
          const birthdayData = await birthdayResponse.json();
          if (birthdayData && birthdayData.length > 0) {
            birthdayChar = birthdayData[0];
            setBirthdayCharacter(birthdayChar);
          }
        }
        
        // Cache the data for future visits
        localStorage.setItem('charactersPageData', JSON.stringify({
          characters: charactersData,
          birthdayChar,
          animeData: animeStore
        }));
        localStorage.setItem('charactersPageTimestamp', Date.now().toString());
        
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    const fetchBirthdayData = async () => {
      try {
        const birthdayResponse = await fetch(`${API_BASE_URL}/api/characters/birthdays/today`);
        
        if (birthdayResponse.ok) {
          const birthdayData = await birthdayResponse.json();
          if (birthdayData && birthdayData.length > 0) {
            setBirthdayCharacter(birthdayData[0]);
            
            // Update cached data with new birthday character
            const cachedData = localStorage.getItem('charactersPageData');
            if (cachedData) {
              try {
                const data = JSON.parse(cachedData);
                data.birthdayChar = birthdayData[0];
                localStorage.setItem('charactersPageData', JSON.stringify(data));
              } catch (e) {
                console.error("Error updating cached birthday data:", e);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error fetching birthday data:", err);
      }
    };

    fetchData();
  }, []);

  // Fetch anime data only for visible characters and birthday character
  useEffect(() => {
    if (characters.length === 0 || animeLoading) return;

    const extractAndFetchAnimeData = async () => {
      const animeIds = new Set();
      
      // Get anime IDs only from current page characters
      currentCharacters.forEach((character) => {
        if (Array.isArray(character.anime)) {
          character.anime.forEach((entry) => {
            if (entry.anime?.mal_id) {
              animeIds.add(entry.anime.mal_id);
            }
          });
        }
      });

      // Also include anime from birthday character if available
      if (birthdayCharacter && Array.isArray(birthdayCharacter.anime)) {
        birthdayCharacter.anime.forEach((entry) => {
          if (entry.anime?.mal_id) {
            animeIds.add(entry.anime.mal_id);
          }
        });
      }

      // Fetch only anime data we don't already have
      const idsToFetch = Array.from(animeIds).filter((id) => !animeStore[id]);
      
      if (idsToFetch.length > 0) {
        setAnimeLoading(true);
        const newAnimeData = await fetchAnimeData(idsToFetch);
        
        // Update the cache with new anime data
        const cachedData = localStorage.getItem('charactersPageData');
        if (cachedData) {
          try {
            const data = JSON.parse(cachedData);
            data.animeData = { ...data.animeData, ...newAnimeData };
            localStorage.setItem('charactersPageData', JSON.stringify(data));
          } catch (e) {
            console.error("Error updating cached anime data:", e);
          }
        }
        
        setAnimeLoading(false);
      }
    };

    extractAndFetchAnimeData();
  }, [currentPage, birthdayCharacter, characters]);

  // Memoize the current characters to avoid recalculation on every render
  const currentCharacters = useMemo(() => {
    const indexOfLastCharacter = currentPage * charactersPerPage;
    const indexOfFirstCharacter = indexOfLastCharacter - charactersPerPage;
    return characters.slice(indexOfFirstCharacter, indexOfLastCharacter);
  }, [characters, currentPage, charactersPerPage]);

  // Memoize the first character index
  const indexOfFirstCharacter = useMemo(() => {
    return (currentPage - 1) * charactersPerPage;
  }, [currentPage, charactersPerPage]);

  // Optimized fetchAnimeData function with better batching
  const fetchAnimeData = async (malIds) => {
    try {
      // Use existing data from store
      const updatedStore = { ...animeStore };

      // Split into batches of 50 max (API limit)
      const BATCH_SIZE = 50;
      const batches = [];

      for (let i = 0; i < malIds.length; i += BATCH_SIZE) {
        batches.push(malIds.slice(i, i + BATCH_SIZE));
      }

      // Process each batch with retry logic
      for (const batch of batches) {
        const idsParam = batch.join(",");
        let retries = 3;
        let success = false;

        while (retries > 0 && !success) {
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/anime/batch?ids=${idsParam}`
            );

            if (!response.ok) {
              if (response.status === 429) {
                retries--;
                const delay = Math.pow(2, 3 - retries) * 1000;
                await new Promise((resolve) => setTimeout(resolve, delay));
                continue;
              }
              throw new Error(`Batch request failed: ${response.status}`);
            }

            const batchData = await response.json();
            Object.assign(updatedStore, batchData);
            success = true;
          } catch (batchError) {
            console.error("Error processing batch:", batchError);
            retries--;
            if (retries > 0) {
              const delay = Math.pow(2, 3 - retries) * 1000;
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }
        }
      }

      setAnimeStore(updatedStore);
      return updatedStore;
    } catch (error) {
      console.error("Error fetching anime data:", error);
      return {};
    }
  };

  // Optimized page change handlers
  const handlePageChange = (pageNumber) => {
    setIsPageChanging(true);
    setCurrentPage(pageNumber);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Add small timeout to show loading state
    setTimeout(() => setIsPageChanging(false), 300);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Memoize pagination buttons to prevent unnecessary recalculation
  const paginationButtons = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(
        (num) =>
          num === 1 ||
          num === totalPages ||
          (num >= currentPage - 1 && num <= currentPage + 1)
      )
      .map((num, i, arr) => (
        <React.Fragment key={num}>
          {i > 0 && arr[i - 1] !== num - 1 && (
            <span className="character-page-ellipsis">•••</span>
          )}
          <button
            onClick={() => handlePageChange(num)}
            className={`character-page-num ${
              currentPage === num ? "active" : ""
            }`}
          >
            {num}
          </button>
        </React.Fragment>
      ));
  }, [currentPage, totalPages]);

  if (isLoading) {
    return <CharactersPageSkeleton />;
  }

  if (error) {
    return (
      <div className="characters-page error">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="characters-page">
      <div className="characters-page-title-container">
        <h1 className="characters-page-title">Top 100 Characters</h1>
      </div>

      {birthdayCharacter && (
        <Suspense fallback={
          <div className="birthday-container skeleton">
            <div className="birthday-display">
              <div className="birthday-header">
                <div className="skeleton-block" style={{ height: '28px', width: '60%' }}></div>
              </div>
              <div className="birthday-content">
                <div className="skeleton-image" style={{ height: '150px', width: '150px' }}></div>
                <div className="birthday-details">
                  <div className="skeleton-block" style={{ height: '24px', width: '80%' }}></div>
                  <div className="skeleton-block" style={{ height: '18px', width: '60%' }}></div>
                </div>
              </div>
            </div>
          </div>
        }>
          <div className="birthday-container">
            <BirthdayDisplay
              character={birthdayCharacter}
              animeStore={animeStore}
            />
          </div>
        </Suspense>
      )}

      {(animeLoading || isPageChanging) && (
        <div className="anime-loading-indicator">
          <div className="loading-spinner-small"></div>
          <p>Loading {isPageChanging ? "characters" : "anime details"}...</p>
        </div>
      )}

      <div className="characters-content">
        {characters.length > 0 ? (
          <>
            <div className="characters-grid">
              {currentCharacters.map((character, index) => (
                <LazyCharacterCard
                  key={character._id || character.mal_id}
                  character={character}
                  index={index}
                  indexOfFirstCharacter={indexOfFirstCharacter}
                  animeStore={animeStore}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="page-btn prev"
                >
                  Previous
                </button>

                <div className="character-page-numbers">
                  {paginationButtons}
                </div>

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="page-btn next"
                >
                  Next
                </button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination-info-container">
                <div className="character-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="no-characters">
            <p>No characters found. Try refreshing the page.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Use React.memo to prevent unnecessary re-renders of the whole component
export default React.memo(CharactersPage);